import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Download, Share2, Search, Users, GitCompareArrows, X, Loader2, Mail, MailCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { TrackBadge } from "@/components/admin/Track";

const Pct = ({ v, label }) => (
  <div className="min-w-[72px]">
    <div className="flex items-baseline justify-between">
      <span className="font-body text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
      <span className="font-heading font-bold text-xs text-slate-800">{v ?? "—"}{v != null && "%"}</span>
    </div>
    <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden">
      <div className="h-full rounded-full bg-primary" style={{ width: `${v ?? 0}%` }} />
    </div>
  </div>
);

export const ReportsPanel = ({ superAdmin }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [trackFilter, setTrackFilter] = useState("all");
  const [selected, setSelected] = useState([]);

  const toggle = (id) => setSelected((s) => {
    if (s.includes(id)) return s.filter((x) => x !== id);
    if (s.length >= 5) { toast.info("You can compare up to 5 candidates at once."); return s; }
    return [...s, id];
  });

  useEffect(() => {
    api.get("/admin/reports").then((r) => setRows(r.data))
      .catch(() => { toast.error("Could not load reports"); setRows([]); });
  }, []);

  const copyShare = async (r) => {
    const url = `${window.location.origin}/shared/${r.share_id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); }
    catch (e) { window.prompt("Copy:", url); }
  };

  const [downloading, setDownloading] = useState(null);
  const downloadPdf = async (r) => {
    setDownloading(r.assessment_id);
    try {
      const res = await api.get(`/admin/assessments/${r.assessment_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `CareerCompass-Report-${(r.candidate_name || "Candidate").replace(/[^\w-]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("Could not generate PDF"); }
    finally { setDownloading(null); }
  };

  const [emailing, setEmailing] = useState(null);
  const emailCandidate = async (r) => {
    let email = r.recipient_email;
    if (!email) { email = window.prompt("Send the report to which email address?", ""); if (!email) return; }
    else if (r.candidate_report_emailed_at && !window.confirm(`Report already emailed to ${email} on ${new Date(r.candidate_report_emailed_at).toLocaleDateString()}. Send again?`)) return;
    setEmailing(r.assessment_id);
    try {
      const res = await api.post(`/admin/assessments/${r.assessment_id}/email-candidate`, { email: r.recipient_email ? "" : email, base_url: window.location.origin });
      toast.success(`Branded PDF report emailed to ${res.data.to}`);
      setRows((rs) => rs.map((x) => x.assessment_id === r.assessment_id ? { ...x, recipient_email: res.data.to, candidate_report_emailed_at: new Date().toISOString() } : x));
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not send", { duration: 9000 }); }
    finally { setEmailing(null); }
  };

  const filtered = (rows || []).filter((r) => {
    if (trackFilter !== "all" && (r.track || "comeback") !== trackFilter) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [r.candidate_name, r.code, r.recipient_email, r.created_by, r.top_match, r.holland_code].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6" data-testid="reports-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-body text-sm text-slate-500"><span className="font-heading font-bold text-slate-900" data-testid="reports-count">{rows?.length ?? 0}</span> completed report{rows?.length === 1 ? "" : "s"}<span className="ml-2 text-slate-400">· tick 2–5 candidates to compare</span></p>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <>
              <Button size="sm" variant="ghost" className="rounded-xl font-heading font-bold" onClick={() => setSelected([])} data-testid="compare-clear-btn"><X className="w-3.5 h-3.5 mr-1" /> Clear</Button>
              <Button size="sm" disabled={selected.length < 2} className="rounded-xl font-heading font-bold" onClick={() => navigate(`/admin/compare?ids=${selected.join(",")}`)} data-testid="compare-btn">
                <GitCompareArrows className="w-4 h-4 mr-1" /> Compare ({selected.length})
              </Button>
            </>
          )}
          <div className="inline-flex rounded-xl bg-slate-100 p-1" data-testid="reports-track-filter">
            {[["all", "All"], ["start", "Start"], ["comeback", "Comeback"]].map(([v, l]) => (
              <button key={v} onClick={() => setTrackFilter(v)} data-testid={`reports-filter-${v}`}
                className={`px-3 py-1.5 rounded-lg font-heading font-bold text-xs transition-colors ${trackFilter === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search candidate, code, career…"
              className="pl-9 rounded-xl w-72" data-testid="reports-search-input" />
          </div>
        </div>
      </div>

      {rows === null ? <p className="font-body text-slate-400">Loading…</p> : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center" data-testid="no-reports">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-body text-slate-500">{rows.length === 0 ? "No candidates have completed the assessment yet." : "No reports match your search."}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((r) => (
            <div key={r.assessment_id} className={`bg-white rounded-2xl border p-5 flex flex-wrap items-center gap-5 transition-colors ${selected.includes(r.assessment_id) ? "border-primary ring-1 ring-primary/30" : "border-slate-200"}`} data-testid={`report-card-${r.code}`}>
              <Checkbox checked={selected.includes(r.assessment_id)} onCheckedChange={() => toggle(r.assessment_id)}
                className="shrink-0" data-testid={`select-${r.code}`} aria-label={`Select ${r.candidate_name} for comparison`} />
              <div className="min-w-[200px] flex-1">
                <p className="font-heading font-extrabold text-slate-900 flex items-center gap-2">{r.candidate_name} <TrackBadge track={r.track} /></p>
                <p className="font-body text-xs text-slate-500 mt-0.5">
                  <span className="font-mono">{r.code}</span>
                  {r.recipient_email ? ` · ${r.recipient_email}` : ""}
                  {" · "}{r.completed_at ? new Date(r.completed_at).toLocaleDateString() : "—"}
                  {superAdmin && r.owner_id && <span className="inline-flex items-center gap-1 ml-2 text-indigo-500"><Users className="w-3 h-3" />{r.created_by}</span>}
                </p>
                {r.headline && <p className="font-body text-sm text-slate-600 mt-2 line-clamp-2">{r.headline}</p>}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <Pct v={r.cognitive} label="Cognitive" />
                <Pct v={r.aptitude} label="Aptitude" />
                <div>
                  <p className="font-body text-[10px] uppercase tracking-wide text-slate-400">DISC · Holland</p>
                  <p className="font-heading font-bold text-sm text-slate-800 mt-0.5">{r.disc_primary || "—"} · <span className="font-mono">{r.holland_code || "—"}</span></p>
                </div>
                <div className="max-w-[190px]">
                  <p className="font-body text-[10px] uppercase tracking-wide text-slate-400">Top match</p>
                  <p className="font-heading font-bold text-sm text-slate-800 mt-0.5 flex items-baseline gap-1">
                    <span className="truncate">{r.top_match || "—"}</span>
                    {r.top_fit ? <span className="text-emerald-600 shrink-0">{r.top_fit}%</span> : null}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 ml-auto">
                <Button size="sm" variant="outline" className="rounded-lg h-9" disabled={emailing === r.assessment_id}
                  title={r.candidate_report_emailed_at ? `Report emailed to candidate ${new Date(r.candidate_report_emailed_at).toLocaleDateString()}` : "Email report to candidate"}
                  onClick={() => emailCandidate(r)} data-testid={`email-candidate-${r.code}`}>
                  {emailing === r.assessment_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : r.candidate_report_emailed_at ? <MailCheck className="w-3.5 h-3.5 text-emerald-500" /> : <Mail className="w-3.5 h-3.5" />}
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg h-9" title="Copy share link" onClick={() => copyShare(r)} data-testid={`share-${r.code}`}>
                  <Share2 className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg h-9 font-heading font-bold" onClick={() => navigate(`/admin/report/${r.assessment_id}`)} data-testid={`open-report-${r.code}`}>
                  <FileText className="w-3.5 h-3.5 mr-1" /> View
                </Button>
                <Button size="sm" className="rounded-lg h-9 font-heading font-bold" disabled={downloading === r.assessment_id} onClick={() => downloadPdf(r)} data-testid={`download-${r.code}`}>
                  {downloading === r.assessment_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} PDF
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
